import React, {useEffect, useId, useMemo, useRef, useState} from "react";
import {HexColorPicker} from "react-colorful";

import {useSetting} from "contexts/setting";
import {
  PRODUCT_PALETTES,
  findProductPalette,
  parseColorInput,
  pushRecentColor,
  readRecentColors,
  writeRecentColors,
} from "libs/colorPicker";
import "./index.css";

type EyeDropperResult = {sRGBHex: string};
type EyeDropperApi = new () => {open: () => Promise<EyeDropperResult>};

function getEyeDropper(): EyeDropperApi | null {
  const api = (window as unknown as {EyeDropper?: EyeDropperApi}).EyeDropper;
  return api ?? null;
}

type SwatchProps = {color: string; selected: boolean; onPick: (hex: string) => void};

const Swatch = ({color, selected, onPick}: SwatchProps) => (
  <button
    type="button"
    className={`color-picker__swatch${selected ? " selected" : ""}`}
    style={{backgroundColor: color}}
    title={color}
    aria-label={color}
    onClick={() => onPick(color)}
  />
);

type ColorPickerPanelProps = {
  value: string;
  onChange: (hex: string) => void;
};

/** Color panel: a saturation/hue area, a hex field that accepts pasted
 * colors, an eyedropper, the current product palette, recent colors, and
 * all product palettes. Use it inside your own popover, or use ColorPicker. */
export const ColorPickerPanel = ({value, onChange}: ColorPickerPanelProps) => {
  const {product} = useSetting();
  const current = value.toLowerCase();
  const [draft, setDraft] = useState(current);
  const [recent, setRecent] = useState<string[]>(readRecentColors);
  const [showAll, setShowAll] = useState(false);
  const [query, setQuery] = useState("");
  const eyeDropper = getEyeDropper();

  useEffect(() => setDraft(current), [current]);

  // True while a pointer button is down anywhere on the page.
  const pointerDownRef = useRef(false);
  useEffect(() => {
    const down = () => {
      pointerDownRef.current = true;
    };
    const up = () => {
      pointerDownRef.current = false;
    };
    window.addEventListener("pointerdown", down, true);
    window.addEventListener("pointerup", up, true);
    window.addEventListener("pointercancel", up, true);
    return () => {
      window.removeEventListener("pointerdown", down, true);
      window.removeEventListener("pointerup", up, true);
      window.removeEventListener("pointercancel", up, true);
    };
  }, []);

  const productPalette = findProductPalette(product?.label);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return PRODUCT_PALETTES;
    return PRODUCT_PALETTES.filter((p) => p.name.toLowerCase().includes(q));
  }, [query]);

  const remember = (hex: string) => {
    setRecent((list) => {
      const next = pushRecentColor(list, hex);
      writeRecentColors(next);
      return next;
    });
  };

  // Discrete picks (swatch, hex, eyedropper) go to the recent list at once.
  // Drags in the area go there when the pointer is released.
  const pick = (hex: string) => {
    onChange(hex);
    remember(hex);
  };

  const pickWithEyeDropper = async () => {
    if (!eyeDropper) return;
    try {
      const result = await new eyeDropper().open();
      const hex = parseColorInput(result.sRGBHex);
      if (hex) pick(hex);
    } catch {
      // The user pressed Escape. Keep the current color.
    }
  };

  return (
    <div className="color-picker">
      <div onPointerUp={() => remember(current)}>
        <HexColorPicker color={current} onChange={onChange} />
      </div>

      <div className="color-picker__row">
        <span className="color-picker__preview" style={{backgroundColor: current}} />
        <input
          type="text"
          value={draft}
          aria-label="Hex color"
          className="color-picker__hex"
          spellCheck={false}
          onChange={(event) => {
            setDraft(event.target.value);
            const hex = parseColorInput(event.target.value);
            if (hex) onChange(hex);
          }}
          onBlur={() => {
            const hex = parseColorInput(draft);
            setDraft(current);
            if (!hex) return;
            // A click blurs this field on pointerdown. A new Recent row then
            // moves the controls below it before pointerup, and the click
            // misses its target ("Use product color" did nothing). So when a
            // pointer is down, record the color after the click.
            if (!pointerDownRef.current) {
              remember(hex);
              return;
            }
            window.addEventListener("pointerup", () => setTimeout(() => remember(hex), 0), {
              once: true,
            });
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
          }}
        />
        {eyeDropper && (
          <button
            type="button"
            className="color-picker__eyedropper"
            title="Pick a color from the screen"
            aria-label="Pick a color from the screen"
            onClick={pickWithEyeDropper}
          >
            <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
              <path
                fill="currentColor"
                d="M13.6 2.4a2 2 0 0 0-2.8 0L9.2 4 8.5 3.3 7.1 4.7l.7.7-5 5a1 1 0 0 0-.3.7V13h1.9a1 1 0 0 0 .7-.3l5-5 .7.7 1.4-1.4-.7-.7 1.6-1.6a2 2 0 0 0 0-2.8ZM4 12H3v-1l5-5 1 1-5 5Z"
              />
            </svg>
          </button>
        )}
      </div>

      {productPalette && (
        <div className="color-picker__section">
          <span className="color-picker__label">{productPalette.name}</span>
          <div className="color-picker__swatches">
            {productPalette.colors.map((c, i) => (
              <Swatch key={`${c}-${i}`} color={c} selected={c === current} onPick={pick} />
            ))}
          </div>
        </div>
      )}

      {recent.length > 0 && (
        <div className="color-picker__section">
          <span className="color-picker__label">Recent</span>
          <div className="color-picker__swatches">
            {recent.map((c) => (
              <Swatch key={c} color={c} selected={c === current} onPick={pick} />
            ))}
          </div>
        </div>
      )}

      <div className="color-picker__section">
        <button
          type="button"
          className="color-picker__toggle"
          aria-expanded={showAll}
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll ? "▾" : "▸"} All product palettes
        </button>
        {showAll && (
          <>
            <input
              type="search"
              value={query}
              placeholder="Search products"
              aria-label="Search products"
              className="color-picker__search"
              onChange={(event) => setQuery(event.target.value)}
            />
            <ul className="color-picker__palettes">
              {filtered.map((p) => (
                <li key={p.name} className="color-picker__palette">
                  <span className="color-picker__palette-name" title={p.name}>
                    {p.name}
                  </span>
                  <span className="color-picker__swatches">
                    {p.colors.map((c, i) => (
                      <Swatch key={`${c}-${i}`} color={c} selected={c === current} onPick={pick} />
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

type ColorPickerProps = ColorPickerPanelProps & {
  ariaLabel: string;
  className?: string;
};

/** Swatch button that opens ColorPickerPanel in a popover. */
const ColorPicker = ({value, onChange, ariaLabel, className}: ColorPickerProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const popoverId = `color-picker-${useId().replace(/:/g, "")}`;

  useEffect(() => {
    const popover = popoverRef.current;
    if (!popover) return;

    // Put the popover below the button. Put it above when there is no space
    // below. Keep it in the viewport horizontally.
    const positionPopover = (event: Event) => {
      if ((event as ToggleEvent).newState !== "open") return;
      const button = buttonRef.current;
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const width = popover.offsetWidth || 240;
      const height = popover.offsetHeight || 380;
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
      const below = rect.bottom + 6;
      const top =
        below + height > window.innerHeight - 8 ? Math.max(8, rect.top - height - 6) : below;
      popover.style.top = `${top}px`;
      popover.style.left = `${left}px`;
    };

    popover.addEventListener("beforetoggle", positionPopover);
    return () => popover.removeEventListener("beforetoggle", positionPopover);
  }, []);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`color-picker__trigger${className ? ` ${className}` : ""}`}
        style={{backgroundColor: value}}
        title={value}
        aria-label={ariaLabel}
        popoverTarget={popoverId}
      />
      <div
        ref={popoverRef}
        id={popoverId}
        popover="auto"
        className="color-picker__popover"
        onPointerDown={(event) => event.stopPropagation()}
      >
        <ColorPickerPanel value={value} onChange={onChange} />
      </div>
    </>
  );
};

export default ColorPicker;
